<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :items="items" class="mc-page-three" />
      <p class="mb-4">
        {{ $t('setting.list.managementAsset.gps.subtitle') }}
      </p>
      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <b-button
            variant="outline-primary"
            class="ms-3"
            @click="$router.push({ name: 'Management Asset' })"
          >
            <i class="mc-fa icon-outline_arrow-left"></i>
            {{ $t('global.back') }}
          </b-button>
        </div>
      </div>
    </div>
    <b-card>
      <GpsGraphqlEditableTable
        v-show="isTableDataAvailable"
        ref="table"
        :fields="fields"
        :apiClient="query"
        trackBy="imei"
        :isPredefined="true"
        :searchable="true"
      />
    </b-card>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'
import GpsGraphqlEditableTable from './components/gps'

import {
  GET_DEVICES_TYPES,
  GET_GPS,
  REGISTER_IMEI_MUTATION_CREATE,
  UPDATE_SUBSCRIPTION,
} from '@/services/api/setting/personalization/gps'
import { DEVICE_MUTATION_UPDATE } from '@/services/api/master/tracking'
import { VEHICLE_MUTATION_UPDATE } from '@/services/api/master/fleet'

export default {
  page: {
    title: 'Manajemen GPS',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    GpsGraphqlEditableTable,
  },

  mixins: [Helper],

  data() {
    return {
      isTableDataAvailable: true,
      data: [],
      companyId: null,
      vehicleOptions: [],
    }
  },

  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      isWhitelabel: 'identity/isWhitelabel',
      isMetrack: 'identity/isMetrack',
    }),
    title() {
      return this.$t('setting.list.managementAsset.gps.title')
    },
    items() {
      return [
        {
          text: this.$t('setting.homePage.pengaturan'),
          to: { name: 'Setting' },
        },
        {
          text: this.$t('setting.homePage.asset'),
          to: { name: 'Management Asset' },
        },
        {
          text: this.$t('setting.list.managementAsset.gps.title'),
          active: true,
        },
      ]
    },
    illustrationPath() {
      if (this.isWhitelabel() || this.isMetrack()) {
        return require(`@/assets/images/illustration/whitelabel/no-data.svg`)
      }
      return require(`@/assets/images/illustration/saas/no-data.svg`)
    },
    fields() {
      return [
        {
          key: 'activeOn',
          keyOrder: 'activeOn',
          label: this.$t('setting.list.managementAsset.gps.installedDate'),
          thClass: 'mc-table--170',
          isSortable: true,
          isEditable: false,
          isDateTime: true,
        },
        {
          key: 'imei',
          keyOrder: 'imei',
          label: this.$t('setting.list.managementAsset.gps.imei'),
          placeholder: this.$t('setting.list.managementAsset.gps.imei'),
          thClass: 'mc-table--170',
          isSortable: true,
          isEditable: true,
          type: 'number',
          validation: [['required']],
          isNumber: true,
        },
        {
          key: 'companyName',
          keyOrder: 'company.name',
          label: this.$t('setting.list.managementAsset.gps.companyName'),
          placeholder: this.$t('setting.list.managementAsset.gps.companyName'),
          thClass: 'mc-table--170',
          isEditable: true,
          isSortable: true,
          type: 'select-resourceful',
          trackBy: 'companyId',
          validation: [['required']],
        },
        {
          key: 'licensePlate',
          keyOrder: 'vehicle.licensePlate',
          label: this.$t('setting.list.managementAsset.gps.licensePlate'),
          placeholder: this.$t('setting.list.managementAsset.gps.licensePlate'),
          thClass: 'mc-table--170',
          isEditable: true,
          isSortable: true,
          type: 'select-resourceful',
          taggable: true,
          trackBy: 'licensePlate',
          validation: [['required']],
        },
        {
          key: 'vehicleType',
          keyOrder: 'vehicle.vehicleType.type',
          label: this.$t('setting.list.managementAsset.gps.vehicleType'),
          placeholder: this.$t('setting.list.managementAsset.gps.vehicleType'),
          thClass: 'mc-table--170',
          isSortable: true,
          isEditable: true,
          type: 'select-resourceful',
          trackBy: 'vehicleTypeId',
          validation: [['required']],
        },
        {
          key: 'vendorType',
          keyOrder: 'vendorType',
          label: this.$t('setting.list.managementAsset.gps.provider'),
          placeholder: this.$t('setting.list.managementAsset.gps.provider'),
          thClass: 'mc-table--170',
          isSortable: true,
          isEditable: true,
          validation: [['required']],
        },
        {
          key: 'deviceName',
          keyOrder: 'device.deviceName',
          label: this.$t('setting.list.managementAsset.gps.deviceType'),
          placeholder: this.$t('setting.list.managementAsset.gps.deviceType'),
          thClass: 'mc-table--170',
          isEditable: true,
          isSortable: true,
          type: 'select-resourceful',
          trackBy: 'deviceCode',
          optionsGetter: this.resourcefulQuery.deviceType,
          optionLabelFn: ({ item }) => item.deviceName,
          getInitialObject: (item) => {
            return { deviceCode: item.deviceCode, deviceName: item.deviceName }
          },
          customValueFn: (item) => item.deviceCode ?? '',
          validation: [['required']],
        },
        {
          key: 'phoneNo',
          keyOrder: 'phoneNo',
          label: this.$t('setting.list.managementAsset.gps.phoneNo'),
          placeholder: this.$t('setting.list.managementAsset.gps.phoneNo'),
          thClass: 'mc-table--170',
          isSortable: true,
          isEditable: true,
          type: 'number',
          validation: [['required']],
          isNumber: true,
        },
        {
          key: 'latitude',
          keyOrder: 'vehicle.vehicleStatus.latitude',
          label: this.$t('setting.list.managementAsset.gps.latitude'),
          placeholder: this.$t('setting.list.managementAsset.gps.latitude'),
          thClass: 'mc-table--170',
          isSortable: true,
          isEditable: false,
        },
        {
          key: 'longitude',
          keyOrder: 'vehicle.vehicleStatus.longitude',
          label: this.$t('setting.list.managementAsset.gps.longitude'),
          placeholder: this.$t('setting.list.managementAsset.gps.longitude'),
          thClass: 'mc-table--170',
          isSortable: true,
          isEditable: false,
        },
        {
          key: 'lastPacket',
          keyOrder: 'vehicle.vehicleStatus.lastPacket',
          label: this.$t('setting.list.managementAsset.gps.lastSeen'),
          placeholder: this.$t('setting.list.managementAsset.gps.lastSeen'),
          thClass: 'mc-table--170',
          isSortable: true,
          isEditable: false,
        },
        {
          key: 'motionStatus',
          keyOrder: 'vehicle.vehicleStatus.motionStatus',
          label: this.$t('setting.list.managementAsset.gps.statusMovement'),
          placeholder: this.$t('setting.list.managementAsset.gps.statusMovement'),
          thClass: 'mc-table--170',
          isSortable: true,
          isEditable: false,
        },
        {
          key: 'odometer',
          keyOrder: 'vehicle.odometer',
          label: this.$t('setting.list.managementAsset.gps.odometer'),
          placeholder: this.$t('setting.list.managementAsset.gps.odometer'),
          thClass: 'mc-table--170',
          isSortable: true,
          isEditable: true,
          type: 'number',
          validation: [['required']],
        },
        {
          key: 'startSubscription',
          keyOrder: 'resellerSubscription.startSubscription',
          label: this.$t('setting.list.managementAsset.gps.startDateSub'),
          placeholder: this.$t('setting.list.managementAsset.gps.startDateSub'),
          thClass: 'mc-table--250',
          isSortable: true,
          isEditable: true,
          type: 'date-picker',
          datepickerType: 'date',
          valueType: 'YYYY-MM-DD',
          format: 'DD MMM YYYY',
        },
        {
          key: 'endSubscription',
          keyOrder: 'resellerSubscription.endSubscription',
          label: this.$t('setting.list.managementAsset.gps.endDateSub'),
          placeholder: this.$t('setting.list.managementAsset.gps.endDateSub'),
          thClass: 'mc-table--250',
          isSortable: true,
          isEditable: true,
          type: 'date-picker',
          datepickerType: 'date',
          valueType: 'YYYY-MM-DD',
          format: 'DD MMM YYYY',
        },
      ]
    },

    query() {
      return {
        get: async (filter) => {
          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_GPS,
            variables: {
              filter: filter,
            },
          })
          let self = this
          let result = res.data.imeiRegistration.imeiRegistration
          self.data = result.data

          return result
        },
        store: async (data) => {
          const res = await this.$apollo.mutate({
            mutation: REGISTER_IMEI_MUTATION_CREATE,
            variables: {
              input: {
                deviceCode: data.deviceCode,
                domainId: data.domainId,
                imei: data.imei,
                odometer: parseFloat(data.odometer),
                phoneNo: data.phoneNo,
                vehicleTypeId: data.vehicleTypeId,
                vendorType: data.vendorType,
                licensePlate: data.licensePlate,
                vehicleId: data.vehicleId === 0 ? null : data.vehicleId,
                startSubscription: data.startSubscription,
                endSubscription: data.endSubscription,
              },
            },
          })
          const result = res.data.registerFromImei
          if (!result.isSuccessful) {
            let message

            const getMessage = result.message.toLowerCase()

            if (getMessage.includes('odometer cannot have a negative value')) {
              message = this.$t('setting.list.managementAsset.gps.message.negativeOdo')
            } else if (getMessage.includes('vehicle does not exist')) {
              message = this.$t('setting.list.managementAsset.gps.message.vehicleNotExist')
            } else if (getMessage.includes('vehicle is not suitable with selected company')) {
              message = this.$t('setting.list.managementAsset.gps.message.vehicleNotSuitable')
            } else if (getMessage.includes('vehicle already has another device')) {
              message = this.$t('setting.list.managementAsset.gps.message.vehicleAlready')
            } else if (
              getMessage.includes('license plate is already registered to another vehicle')
            ) {
              message = this.$t('setting.list.managementAsset.gps.message.licenseAlready')
            } else if (getMessage.includes('imei already registered to another device')) {
              message = this.$t('setting.list.managementAsset.gps.message.imeiAlready')
            } else {
              message = getMessage
            }

            this.$mcToast.warning(message)
            return
          }

          return result
        },
        update: async (imei, oldData, newData) => {
          const inputDevice = {
            imei: imei,
            isFromGpsManagement: true,
            newData: {
              phoneNo: newData.phoneNo,
              deviceCode: newData.deviceName,
              vendorType: newData.vendorType,
            },
            oldData: {
              phoneNo: oldData.phoneNo,
              deviceCode: oldData.deviceCode,
              vendorType: oldData.vendorType,
            },
          }

          const inputVehicle = {
            id: oldData.vehicleId,
            isFromGpsManagement: true,
            newData: {
              odometer: parseFloat(newData.odometer),
            },
            oldData: {
              odometer: oldData.odometer ? parseFloat(oldData.odometer) : 0,
            },
          }

          const updateDevice = await this.$apollo.mutate({
            mutation: DEVICE_MUTATION_UPDATE,
            variables: {
              input: inputDevice,
            },
          })

          const resultDevice = updateDevice.data.updateDevice.isSuccessful

          const updateVehicle = await this.$apollo.mutate({
            mutation: VEHICLE_MUTATION_UPDATE,
            variables: {
              input: inputVehicle,
            },
          })

          const resultVehicle = updateVehicle.data.updateVehicle.isSuccessful

          const inputSubscription = {
            imei: imei,
            newData: {
              startSubscription: newData.startSubscription,
              endSubscription: newData.endSubscription,
            },
            oldData: {
              startSubscription: oldData.startSubscription,
              endSubscription: oldData.endSubscription,
            },
            isFromGpsManagement: true,
          }

          const updateSubscription = await this.$apollo.mutate({
            mutation: UPDATE_SUBSCRIPTION,
            variables: {
              input: inputSubscription,
            },
          })

          const resultSubscription = updateSubscription.data.updateResellerSubscription.isSuccessful

          return resultDevice, resultVehicle, resultSubscription
        },
      }
    },

    resourcefulQuery() {
      return {
        deviceType: async (opts) => {
          const filter = {
            deviceName: opts.search,
          }

          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_DEVICES_TYPES,
            variables: {
              filter: filter,
            },
          })

          const data = res.data.deviceTypes.deviceType
          const result = {
            metadata: {
              total_page: 1,
              total_count: 5,
              page: 1,
            },
            data: {
              paginated_result: data,
            },
          }

          return result
        },
      }
    },
  },
}
</script>
